import React from "react";
import { Page, Text, View, Document, StyleSheet, Image } from "@react-pdf/renderer";
import paymentMethods from "utils/static/paymentMethods";
import { dateToString } from "utils/dateUtils";
import { Tenant } from "utils/tenants";
import { formatCurrency } from "utils/currency";

const getPaymentMethodName = (paymentMethod) => (paymentMethod ? paymentMethods.find((x) => x.value === paymentMethod).label : "");
const Money = ({ amount, currency }) => formatCurrency(amount, currency.locale, currency.isoCode);

// Create styles
const styles = StyleSheet.create({
    page: {
        paddingTop: 65,
        paddingBottom: 65,
        paddingHorizontal: 60,
    },
    header: {
        fontSize: 10,
        flexDirection: "row",
        marginBottom: 10,
        justifyContent: "flex-start",
    },
    image: {
        height: 64,
    },
    companyInfo: {
        marginLeft: 10,
    },
    companyName: {
        fontSize: 14,
        fontWeight: "bold",
    },
    title: {
        fontSize: 20,
    },
    divider: {
        width: "100%",
        height: 1,
        backgroundColor: "black",
        marginVertical: 10,
    },
    customerInfo: {
        fontSize: 10,
        fontWeight: "normal",
        flexDirection: "row",
        marginVertical: 20,
        justifyContent: "space-between",
    },
    customerName: {
        fontSize: 18,
        fontWeight: "bold",
    },
    fileInfo: {
        flexDirection: "row",
    },

    receivedSection: {
        fontSize: 14,
        fontWeight: "normal",
        flexDirection: "row",
        marginTop: 50,
        marginLeft: 10,
        justifyContent: "flex-start",
        width: "60%",
    },

    signature: {
        flex: 1,
        textAlign: "center",
    },

    userName: {
        fontSize: 8,
    },

    balance: {
        flexDirection: "row-reverse",
        marginTop: 10,
    },

    pageNumber: {
        position: "absolute",
        fontSize: 10,
        bottom: 30,
        left: 0,
        right: 0,
        textAlign: "center",
        color: "grey",
    },

    footer: {
        position: "absolute",
        bottom: 80,
        left: 60,
        right: 60,
    },

    detail: {
        width: "100%",
    },

    tableHead: {
        flexDirection: "row",
        alignContent: "flex-start",
        marginBottom: 10,
    },

    columnHeader: {
        fontSize: 12,
        fontWeight: "bold",
        color: "blue",
        fontStyle: "italic",
        padding: 2,
        flex: 1,
        textAlign: "left",
        marginLeft: 10,
    },

    row: {
        flexDirection: "row",
        alignContent: "flex-start",
    },

    cell: {
        fontSize: 11,
        padding: 2,
        flex: 1,
        textAlign: "left",
        marginLeft: 10,
    },

    disclaimer: {
        fontSize: 10,
        fontWeight: "bold",
        fontStyle: "italic",
        textAlign: "left",
        marginTop: 30,
        marginLeft: 10,
    },
});

// Create Document Component
const MyDocument = ({ personalInfo, receiptsData, currency, balance, isTaxes = false }) => (
    <Document>
        {receiptsData &&
            Array.isArray(receiptsData) &&
            receiptsData.map((receipt) => {
                return (
                    <Page key={receipt.id} size="A4" style={styles.page}>
                        <View style={styles.header} fixed>
                            {<Image style={styles.image} src={Tenant.getLogo()} />}
                            {/* {isTaxes && (
                                <View style={{ ...styles.companyInfo, ...styles.image }}>
                                    <Text fixed>Houston</Text>
                                    <Text fixed>Multiservices</Text>
                                </View>
                            )} */}
                            <View style={styles.companyInfo}>
                                {
                                    <Text fixed style={styles.companyName}>
                                        {receipt.issuer?.name}
                                    </Text>
                                }
                                <Text fixed>{receipt.issuer?.address}</Text>
                                <Text fixed>Phone: {receipt.issuer?.phoneNumber}</Text>
                                <Text fixed>Fax: {receipt.issuer?.fax}</Text>
                                <Text fixed>Email: {receipt.issuer?.email}</Text>
                            </View>
                            <View style={{ flex: 1 }} />
                            <View>
                                <Text fixed style={styles.title}>
                                    Receipt
                                </Text>
                                <Text fixed style={{ fontSize: 10, alignSelf: "flex-end" }}>
                                    No. {receipt.id}
                                </Text>
                                <Text fixed style={{ fontSize: 10, alignSelf: "flex-end" }}>
                                    {dateToString(new Date(), false)}
                                </Text>
                            </View>
                        </View>

                        <View style={styles.divider} fixed />

                        <View style={styles.customerInfo} fixed>
                            <View>
                                <Text fixed style={styles.customerName}>
                                    {`${personalInfo.client.firstName} ${personalInfo.client.middleName} ${personalInfo.client.lastName}`}
                                </Text>
                                <Text fixed>{personalInfo.client.address}</Text>
                                <Text fixed>
                                    {`${personalInfo.client.city}. ${personalInfo.client.state} ${personalInfo.client.zipCode}`}
                                </Text>
                            </View>
                            <View style={{ flex: 1 }} />
                            <View style={styles.fileInfo}>
                                <Text fixed style={{ fontSize: 12, marginRight: 20 }}>
                                    File No
                                </Text>
                                <Text fixed style={{ fontSize: 14 }}>
                                    {personalInfo.fileNo}
                                </Text>
                            </View>
                        </View>

                        <View style={{ ...styles.divider, marginTop: 0, borderWidth: 2 }} fixed />

                        <View style={styles.detail}>
                            <View style={styles.tableHead}>
                                <View style={{ ...styles.columnHeader, flexBasis: "10%" }}>
                                    <Text>Date</Text>
                                </View>
                                <View style={{ ...styles.columnHeader, textAlign: "right", flexBasis: "20%" }}>
                                    <Text>Amount Paid</Text>
                                </View>
                                <View style={{ ...styles.columnHeader, flexBasis: "20%" }}>
                                    <Text>Payment Method</Text>
                                </View>
                                <View style={{ ...styles.columnHeader, flexBasis: "45%" }}>
                                    <Text>Description</Text>
                                </View>
                            </View>
                            <View style={styles.row}>
                                <View style={{ ...styles.cell, flexBasis: "10%" }}>
                                    <Text>{dateToString(receipt.date, false)}</Text>
                                </View>
                                <View
                                    style={{
                                        ...styles.cell,
                                        textAlign: "right",
                                        fontWeight: "bold",
                                        flexBasis: "20%",
                                    }}
                                >
                                    <Text>
                                        {receipt.amount && Number(receipt.amount) > 0 ? (
                                            <Money amount={receipt.amount} currency={currency} />
                                        ) : (
                                            ""
                                        )}
                                    </Text>
                                </View>
                                <View style={{ ...styles.cell, flexBasis: "20%" }}>
                                    <Text>{getPaymentMethodName(receipt.paymentMethod)}</Text>
                                </View>
                                <View style={{ ...styles.cell, flexBasis: "45%" }}>
                                    <Text>{receipt.description}</Text>
                                </View>
                            </View>
                        </View>

                        <View style={styles.footer}>
                            <View style={styles.divider} />
                            <View style={styles.balance}>
                                <Text style={{ fontSize: 14 }}>
                                    <Money amount={balance} currency={currency} />
                                </Text>
                                <Text style={{ fontSize: 12, marginRight: 20 }}>Balance:</Text>
                            </View>
                            <View style={styles.receivedSection}>
                                <Text style={{ fontSize: 12, marginRight: 10 }}>Received By:</Text>
                                <View style={styles.signature}>
                                    <View style={styles.divider} />
                                    <Text style={styles.userName}>{receipt.user.name}</Text>
                                </View>
                            </View>
                            <View style={styles.disclaimer}>
                                <Text>Keep this receipt for your records as proof of payment.</Text>
                            </View>
                        </View>

                        <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} fixed />
                    </Page>
                );
            })}
    </Document>
);

export default MyDocument;
